import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Food Preferences v2 API client (api-food-preferences-v2-service).
 *
 * Contract notes:
 *
 * 1. Different from every other v2 section: single-doc-per-patient with
 *    full-doc PUT semantics. There is no flat list, no per-entry create /
 *    update / delete, no trend. `put` replaces the whole doc.
 *
 * 2. Optimistic-lock via `expectedVersion` on `put`. Server returns 409 with
 *    the fresh doc on mismatch — caller should re-apply the user's in-flight
 *    edits on top of the returned snapshot.
 *
 * 3. `getConfig` returns the enum options (diet types, non-veg sub-options,
 *    cuisines, restrictions). Backend is source of truth; the frontend caches
 *    the result per-session so tab-switches don't re-fetch it.
 */
class FoodPreferencesService {
  constructor() {
    this.apiService = new APIService();
  }

  getConfig(onResponse, onError) {
    const url = APIConfig.foodPreferencesV2.config;
    return this.apiService.get(url, onResponse, onError);
  }

  get(patientId, onResponse, onError) {
    const url = APIConfig.foodPreferencesV2.get.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Full-doc replace. Body shape matches beans.PutRequest:
   *   { dietaryPattern: { dietType, nonVegSubOptions[], cuisinePreferences[],
   *                       restrictions[] },
   *     foodBehavior:   { likes[], dislikes[], allergies[], avoidances[],
   *                       limited[], remarks },
   *     expectedVersion }
   *
   * cuisinePreferences chips are `{name, catalogId}` — both required, from
   * the federated bridge catalog served on /config. FoodBehavior chips are
   * `{name, source, catalogId?}` where source ∈ "catalog" | "free-text".
   * The transformer server-side trims, dedupes, and clears NonVegSubOptions
   * when DietType != "non-vegetarian".
   */
  put(patientId, body, onResponse, onError) {
    const url = APIConfig.foodPreferencesV2.put.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }
}

export default FoodPreferencesService;
